import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Loader2, ThumbsUp, Trash2, Archive, FolderOpen, Merge, Pencil, Check } from 'lucide-react'
import { StyledSelect } from '../ui/StyledSelect'
import {
  getFeedbackDetail,
  updateFeedback,
  updateStatus,
  deleteFeedback,
  type FeedbackDetail,
} from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'
import { FeedbackVoteButton } from './FeedbackVoteButton'
import { FeedbackImageGallery } from './FeedbackImageGallery'
import { FeedbackComments } from './FeedbackComments'
import { AdminInternalNotes } from './AdminInternalNotes'
import { useUser } from '../../hooks/useUser'

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  bug: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Bug' },
  feature: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  improvement: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Improvement' },
  integration: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Integration' },
}

const STATUS_OPTIONS = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: '_sep', label: '───────', disabled: true },
  { value: 'dismissed', label: 'Dismiss → Archive' },
]

const MIN_WIDTH = 360
const MAX_WIDTH = 720
const DEFAULT_WIDTH = 480

interface FeedbackDrawerProps {
  feedbackId: number | null
  refreshKey?: number
  onClose: () => void
  onStatusChange?: () => void
  onVoteChange?: (id: number, hasVoted: boolean, voteCount: number) => void
  currentUserId?: number
  onWidthChange?: (width: number) => void
  onMergeRequested?: (item: FeedbackDetail) => void
}

export function FeedbackDrawer({
  feedbackId,
  refreshKey,
  onClose,
  onStatusChange,
  onVoteChange,
  currentUserId: _currentUserId,
  onWidthChange,
  onMergeRequested,
}: FeedbackDrawerProps) {
  const user = useUser()
  const [detail, setDetail] = useState<FeedbackDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'notes'>('details')
  const [deleting, setDeleting] = useState(false)
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [editingField, setEditingField] = useState<'title' | 'description' | 'type' | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const isResizing = useRef(false)
  const drawerRef = useRef<HTMLDivElement>(null)

  const isOpen = feedbackId !== null

  // Reactive mobile detection (updates on window resize)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  )
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Notify parent of width changes for content squishing (desktop only)
  useEffect(() => {
    onWidthChange?.(isOpen && !isMobile ? width : 0)
    return () => { onWidthChange?.(0) }
  }, [isOpen, width, isMobile]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = useCallback(async () => {
    if (!feedbackId) return
    const nextDetail = await getFeedbackDetail(feedbackId)
    setDetail(nextDetail)
  }, [feedbackId])

  useEffect(() => {
    if (!feedbackId) {
      setDetail(null)
      setError(null)
      setLoading(false)
      return
    }
    const isSameItem = detail?.id === feedbackId
    if (!isSameItem) {
      setLoading(true)
    }
    setError(null)
    loadDetail()
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => {
        if (!isSameItem) {
          setLoading(false)
        }
      })
  }, [feedbackId, refreshKey, loadDetail]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (feedbackId) {
      setActiveTab('details')
    }
  }, [feedbackId])

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const startX = e.clientX
    const startWidth = width

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return
      const delta = startX - e.clientX
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta))
      setWidth(newWidth)
    }

    const handleMouseUp = () => {
      isResizing.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [width])

  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      if (!detail || updatingStatus) return
      setUpdatingStatus(true)
      try {
        const updated = await updateStatus(detail.id, newStatus)
        setDetail((prev) => (prev ? { ...prev, status: updated.status } : prev))
        onStatusChange?.()
      } catch {
        // Status change failed silently
      } finally {
        setUpdatingStatus(false)
      }
    },
    [detail, updatingStatus, onStatusChange]
  )

  const handleVoteChange = useCallback(
    (hasVoted: boolean, voteCount: number) => {
      if (!detail) return
      setDetail((prev) =>
        prev ? { ...prev, has_voted: hasVoted, vote_count: voteCount } : prev
      )
      onVoteChange?.(detail.id, hasVoted, voteCount)
    },
    [detail, onVoteChange]
  )

  const refreshDetail = useCallback(() => {
    if (!feedbackId) return
    loadDetail().catch(() => {})
  }, [feedbackId, loadDetail])

  const handleDelete = useCallback(async () => {
    if (!detail || deleting) return
    if (!window.confirm(`Permanently delete "${detail.title}"? This cannot be undone.`)) return
    setDeleting(true)
    try {
      await deleteFeedback(detail.id)
      onStatusChange?.()
      onClose()
    } catch {
      // Delete failed
    } finally {
      setDeleting(false)
    }
  }, [detail, deleting, onStatusChange, onClose])

  const startFieldEdit = useCallback((field: 'title' | 'description' | 'type') => {
    if (!detail) return
    if (field === 'title') setEditValue(detail.title)
    else if (field === 'description') setEditValue(detail.description)
    else setEditValue(detail.type || '')
    setEditingField(field)
  }, [detail])

  const cancelFieldEdit = useCallback(() => {
    setEditingField(null)
  }, [])

  const saveFieldEdit = useCallback(async () => {
    if (!detail || saving || !editingField) return
    const trimmed = editValue.trim()
    if (editingField === 'title' && !trimmed) return
    setSaving(true)
    try {
      const payload: Record<string, string | null> = {}
      if (editingField === 'type') {
        payload.type = trimmed || null
      } else {
        payload[editingField] = trimmed
      }
      const updated = await updateFeedback(detail.id, payload)
      setDetail((prev) => prev ? { ...prev, title: updated.title, description: updated.description, type: updated.type } : prev)
      setEditingField(null)
      onStatusChange?.()
    } catch {
      // Save failed — stay in edit mode
    } finally {
      setSaving(false)
    }
  }, [detail, saving, editingField, editValue, onStatusChange])

  // Reset edit mode when switching items
  useEffect(() => {
    setEditingField(null)
  }, [feedbackId])

  // Escape key: cancel field edit if editing, otherwise close drawer
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingField) {
          e.preventDefault()
          cancelFieldEdit()
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, editingField, onClose, cancelFieldEdit])

  const isOwner = detail?.author?.email ? detail.author.email === user.email : false
  const canEdit = isOwner && !['archived', 'dismissed'].includes(detail?.status || '')

  if (!isOpen) return null

  const typeBadge = detail?.type ? TYPE_BADGE[detail.type] : null
  const authorDisplay =
    detail?.is_anonymous && !detail?.author?.name
      ? 'Anonymous'
      : detail?.author?.name || detail?.author_name || 'Unknown'
  const authorEmail = detail?.author?.email || detail?.author_email || null

  const tabs = [
    { key: 'details' as const, label: 'Details' },
    { key: 'comments' as const, label: `Comments${detail ? ` (${detail.comment_count})` : ''}` },
    ...(user.isAdmin ? [{ key: 'notes' as const, label: 'Internal Notes' }] : []),
  ]

  return createPortal(
    <>
      <div
        ref={drawerRef}
        style={{ width: isMobile ? undefined : `${width}px` }}
        className="fixed inset-0 sm:inset-y-0 sm:left-auto sm:right-0 z-40 bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
      >
        {/* Resize handle (desktop only) */}
        <div
          onMouseDown={handleResizeStart}
          className="hidden sm:block absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors z-50 group"
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0 bg-card">
          <div className="flex items-center gap-2 min-w-0">
            {detail && (
              <span className="text-xs text-muted-foreground shrink-0">#{detail.id}</span>
            )}
            {editingField === 'type' ? (
              <div className="flex items-center gap-1.5">
                <select
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="text-xs bg-secondary/50 border border-border rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                >
                  <option value="">None</option>
                  <option value="bug">Bug</option>
                  <option value="feature">Feature</option>
                  <option value="improvement">Improvement</option>
                  <option value="integration">Integration</option>
                </select>
                <button onClick={saveFieldEdit} disabled={saving} className="p-0.5 text-primary hover:text-primary/80 transition-colors disabled:opacity-50" title="Save">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={cancelFieldEdit} disabled={saving} className="p-0.5 text-muted-foreground hover:text-foreground transition-colors" title="Cancel">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="group/type flex items-center gap-1 shrink-0">
                {typeBadge ? (
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${typeBadge.bg} ${typeBadge.text}`}>
                    {typeBadge.label}
                  </span>
                ) : canEdit ? (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-secondary text-muted-foreground">
                    No type
                  </span>
                ) : null}
                {canEdit && (
                  <button onClick={() => startFieldEdit('type')} className="p-0.5 rounded opacity-0 group-hover/type:opacity-100 hover:bg-secondary transition-all" title="Edit type">
                    <Pencil className="w-2.5 h-2.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-secondary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Meta: submitter, date, status, votes */}
        {detail && !loading && (
          <div className="px-4 py-3 border-b border-border shrink-0 space-y-3">
            {/* Title — inline editable */}
            {editingField === 'title' ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  maxLength={200}
                  className="w-full text-base font-semibold leading-snug bg-secondary/50 border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveFieldEdit() }}
                />
                <div className="flex items-center gap-2">
                  <button onClick={saveFieldEdit} disabled={saving || !editValue.trim()} className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50">
                    <Check className="w-3 h-3" />{saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={cancelFieldEdit} disabled={saving} className="px-2 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="group/title flex items-start gap-1.5">
                <h3 className="text-base font-semibold leading-snug">{detail.title}</h3>
                {canEdit && (
                  <button onClick={() => startFieldEdit('title')} className="shrink-0 p-0.5 rounded opacity-0 group-hover/title:opacity-100 hover:bg-secondary transition-all" title="Edit title">
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            )}

            {/* Meta row */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              <span>{authorDisplay}</span>
              {user.isAdmin && authorEmail && (
                <span className="opacity-75">{authorEmail}</span>
              )}
              <span>{formatRelativeTime(detail.created_at)}</span>
            </div>

            {/* Status + votes + actions row */}
            <div className="flex items-center gap-2 flex-wrap">
              {user.isAdmin ? (
                <StyledSelect
                  value={detail.status}
                  onChange={(val) => handleStatusChange(val)}
                  options={STATUS_OPTIONS}
                  disabled={updatingStatus}
                />
              ) : (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-secondary text-muted-foreground">
                  {STATUS_OPTIONS.find((s) => s.value === detail.status)?.label || detail.status}
                </span>
              )}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ThumbsUp className="w-3.5 h-3.5" />
                <span>{detail.vote_count} {detail.vote_count === 1 ? 'vote' : 'votes'}</span>
              </div>
              {user.isAdmin && (
                <div className="ml-auto flex items-center gap-2">
                  {onMergeRequested && (
                    <button
                      onClick={() => onMergeRequested(detail)}
                      className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors"
                    >
                      <Merge className="w-3.5 h-3.5" />
                      Merge
                    </button>
                  )}
                  {!['dismissed', 'archived'].includes(detail.status) && (
                    <button
                      onClick={() => handleStatusChange('archived')}
                      disabled={updatingStatus}
                      className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-50"
                    >
                      <Archive className="w-3.5 h-3.5" />
                      Archive
                    </button>
                  )}
                  {['dismissed', 'archived'].includes(detail.status) && (
                    <>
                      <button
                        onClick={() => handleStatusChange(detail.previous_status || 'backlog')}
                        disabled={updatingStatus}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20 transition-colors disabled:opacity-50"
                      >
                        Unarchive
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-500/10 rounded-md hover:bg-red-500/20 transition-colors disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Themes */}
            {detail.themes && detail.themes.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <FolderOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                {detail.themes.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary"
                  >
                    #{t.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-border shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="p-3 text-sm bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}

          {detail && !loading && activeTab === 'details' && (
            <div className="space-y-5">
              {/* Description — inline editable */}
              {editingField === 'description' ? (
                <div className="space-y-1.5">
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    maxLength={5000}
                    rows={6}
                    className="w-full text-sm bg-secondary/50 border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary resize-y leading-relaxed"
                    placeholder="Describe your feedback..."
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <button onClick={saveFieldEdit} disabled={saving || !editValue.trim()} className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50">
                      <Check className="w-3 h-3" />{saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={cancelFieldEdit} disabled={saving} className="px-2 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="group/desc">
                  <span className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                    {detail.description}
                  </span>
                  {canEdit && (
                    <button onClick={() => startFieldEdit('description')} className="inline-flex ml-1 p-0.5 rounded opacity-0 group-hover/desc:opacity-100 hover:bg-secondary transition-all align-middle" title="Edit description">
                      <Pencil className="w-3 h-3 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}

              {/* Images */}
              {(detail.images.length > 0 || canEdit) && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Attachments
                  </h4>
                  <FeedbackImageGallery
                    feedbackId={detail.id}
                    images={detail.images}
                    canEdit={canEdit}
                    pasteEnabled={activeTab === 'details'}
                    onImagesChanged={refreshDetail}
                  />
                </div>
              )}

              {/* Vote button */}
              <div className="pt-2">
                <FeedbackVoteButton
                  feedbackId={detail.id}
                  initialHasVoted={detail.has_voted}
                  initialVoteCount={detail.vote_count}
                  onVoteChange={handleVoteChange}
                />
              </div>
            </div>
          )}

          {detail && !loading && activeTab === 'comments' && (
            <FeedbackComments
              feedbackId={detail.id}
              comments={detail.comments}
              onCommentAdded={refreshDetail}
            />
          )}

          {detail && !loading && activeTab === 'notes' && user.isAdmin && (
            <AdminInternalNotes feedbackId={detail.id} isAdmin={user.isAdmin} />
          )}
        </div>
      </div>

    </>,
    document.body
  )
}
